"use client";

// ── Same-document embed entry ──────────────────────────────────────────────
// Loaded by a <script src> in a Webflow Embed element. Finds every
// `[data-resources-embed]` container on the page, fetches the cards from this
// app's `/api/cards`, and mounts the island into the Webflow page's own DOM.
//
// The point of same-document over the iframe it replaces: the grid is part of
// the host page. Clicks, focus, hover, scroll anchoring and the site's own
// sticky/overlay chrome all work without special handling, and the container
// grows with its content the way any other block does — no height messaging, no
// fixed starting height, no inner scrollbar.
//
// Two things follow from living in someone else's document, and both are load-
// bearing:
//
//   1. NO global CSS. This bundle injects `globals.css` + the DevLink CSS
//      Modules output only. It must never pull in `@devlink/global-css` — the
//      Webflow page already ships the site's reset and custom properties, and a
//      second reset would restyle the entire page. The app's own rules are
//      scoped under `.ct-resources` (see globals.css) so they cannot leak out.
//   2. NO <main>. The host page already has its document landmarks, so the
//      island renders a plain <div> here (`embedded`), and its heading — when
//      one is asked for at all — is an <h2>, never a second <h1>.

import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";
import ResourcesIsland, {
  SCOPE_CLASS,
  type ResourcesControls,
  type ResourcesState,
} from "@/components/ResourcesIsland";
import {
  parseFilterValue,
  parseSearchParam,
  parseSortValue,
} from "@/lib/resources";
import { apiUrl } from "@/lib/api-url";
import type { FilterValue, ResourceCard, SortValue } from "@/lib/types";
import "@/styles/globals.css";

const CONTAINER_SELECTOR = "[data-resources-embed]";
const MOUNTED_ATTR = "data-resources-embed-mounted";
const READY_EVENT = "resources-embed:ready";

type EmbedConfig = {
  endpoint: string;
  heading: string | null;
  intro: string | null;
  initialFilter: FilterValue;
  initialQuery: string;
  initialSort: SortValue;
  pageSize: number | undefined;
  syncUrl: boolean;
  minHeight: string | null;
};

// ── Configuration, read off the container ──────────────────────────────────
// Every knob is a data attribute so the whole setup is one HTML snippet a
// Webflow editor can paste and adjust without a redeploy.
function readConfig(el: HTMLElement): EmbedConfig {
  const d = el.dataset;

  // `data-api` covers the case where the embedding page is not on the same host
  // as the mount — a different Webflow site, or a staging page pointed at
  // production data. Left unset it resolves through the mount baked in at build
  // time, which is the same-host default.
  const apiBase = d.api?.replace(/\/+$/, "");
  const endpoint = apiBase ? `${apiBase}/api/cards` : apiUrl("/api/cards");

  // Initial filter/search come from the HOST page's URL, so a link to
  // `/resources?type=webinar` lands pre-filtered. A data attribute overrides
  // that when a page wants a fixed slice regardless of the URL.
  const params = new URLSearchParams(window.location.search);
  const pageSize = Number(d.pageSize);

  return {
    endpoint,
    heading: d.heading ?? null,
    intro: d.intro ?? null,
    initialFilter: parseFilterValue(d.type ?? params.get("type") ?? undefined),
    // `data-search` is the attribute; `data-q` still honoured for continuity
    // with snippets written before the rename.
    initialQuery: d.search ?? d.q ?? parseSearchParam(params),
    initialSort: parseSortValue(d.sort ?? params.get("sort") ?? undefined),
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : undefined,
    // On by default: the embedding page IS the canonical resources page, so a
    // filtered view there deserves a shareable URL. `data-sync-url="false"` for
    // pages where something else owns the query string.
    syncUrl: d.syncUrl !== "false",
    minHeight: d.minHeight ?? null,
  };
}

// ── Layout-change notification ─────────────────────────────────────────────
// The container grows on its own — this is not about making that happen. It is
// about the rest of the Webflow page finding out. Webflow's IX2 caches element
// offsets for scroll-triggered interactions, and sticky/parallax elements below
// the embed compute against a page height that changes when someone filters or
// loads more. Both recompute on `resize`.
//
// Guarded two ways against feedback loops: only fire when the height actually
// changed, and coalesce to one dispatch per frame.
function watchHeight(el: HTMLElement) {
  if (typeof ResizeObserver === "undefined") return;

  let lastHeight = 0;
  let frame = 0;

  const observer = new ResizeObserver(() => {
    const height = Math.round(el.getBoundingClientRect().height);
    if (height === lastHeight) return;
    lastHeight = height;

    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = 0;
      // Namespaced event first, for anything on the page that wants the height
      // without listening to every resize.
      el.dispatchEvent(
        new CustomEvent("resources-embed:resize", {
          bubbles: true,
          detail: { height },
        }),
      );
      window.dispatchEvent(new Event("resize"));
    });
  });

  observer.observe(el);
}

// ── Placeholder + failure states ───────────────────────────────────────────

/**
 * Reserves height while the cards are in flight so the content below the embed
 * does not jump when they land. `data-min-height` sets it; without one the
 * container simply grows from zero, which is correct for an embed placed at the
 * end of a page.
 */
function renderLoading(el: HTMLElement, cfg: EmbedConfig) {
  el.setAttribute("aria-busy", "true");
  if (cfg.minHeight) el.style.minHeight = cfg.minHeight;
}

function clearLoading(el: HTMLElement) {
  el.removeAttribute("aria-busy");
  el.style.minHeight = "";
}

/**
 * A fetch failure leaves a hole in the page either way, so it says so quietly
 * rather than rendering an empty region that looks intentional. The detail goes
 * to the console — the visible copy is for a visitor, not a developer.
 */
function renderError(el: HTMLElement, err: unknown) {
  console.error("[resources-embed] could not load resources:", err);
  clearLoading(el);
  const p = document.createElement("p");
  p.className = "ct-resources__embed-error";
  p.setAttribute("role", "status");
  p.textContent = "Resources are temporarily unavailable. Please try again shortly.";
  el.replaceChildren(p);
}

// ── Mount ──────────────────────────────────────────────────────────────────

type CardsResponse = { ok: boolean; cards?: ResourceCard[]; error?: string };

/** Cards are fetched once per page even if several containers are present. */
let cardsPromise: Promise<ResourceCard[]> | null = null;

function fetchCards(endpoint: string): Promise<ResourceCard[]> {
  if (!cardsPromise) {
    cardsPromise = fetch(endpoint, {
      headers: { accept: "application/json" },
      // The response is edge-cached; let the browser reuse it too.
      credentials: "omit",
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => null)) as CardsResponse | null;
        if (!res.ok || !body?.ok || !Array.isArray(body.cards)) {
          throw new Error(
            body?.error ?? `${endpoint} responded ${res.status} ${res.statusText}`,
          );
        }
        return body.cards;
      })
      // A failed fetch must not poison later mount attempts.
      .catch((err) => {
        cardsPromise = null;
        throw err;
      });
  }
  return cardsPromise;
}

// ── External control ───────────────────────────────────────────────────────
// The tabs, sort list and search field on the Webflow page are Designer
// elements OUTSIDE this container. Same-document means they can drive the grid
// directly — no frame, no postMessage — through `window.ResourcesEmbed`.
//
// The queue is the part that matters. Cards are fetched before the island
// mounts (see `mount`), so there is a real window, typically a few hundred
// milliseconds, in which the page is interactive and a visitor can click a tab
// that nothing is listening to yet. Buffering means an early click applies on
// arrival instead of vanishing. Pages that would rather disable their controls
// until then can listen for `resources-embed:ready`.

let controls: ResourcesControls | null = null;
const pending: Array<(c: ResourcesControls) => void> = [];

function withControls(fn: (c: ResourcesControls) => void): void {
  if (controls) {
    fn(controls);
    return;
  }
  pending.push(fn);
}

/**
 * Adopt the island's controls and drain anything queued while it was mounting.
 *
 * Stable module-level identity on purpose: the island registers in an effect
 * keyed on this callback, so a new function per render would re-register on
 * every state change.
 *
 * With several containers on one page the last to mount wins. That is a real
 * limitation and an accepted one — a single global cannot mean two grids, and
 * every page here has exactly one. Per-container control would need the API
 * keyed by element.
 */
function adoptControls(next: ResourcesControls): void {
  controls = next;
  for (const fn of pending.splice(0)) fn(next);
  window.dispatchEvent(
    new CustomEvent<ResourcesState>(READY_EVENT, { detail: next.getState() }),
  );
}

/**
 * Mount one container. Idempotent: a second call on the same element is a no-op,
 * which matters because a Webflow Embed element can end up duplicated on a page
 * and because `mount()` is exposed for manual calls.
 */
export async function mount(el: HTMLElement): Promise<Root | null> {
  if (el.hasAttribute(MOUNTED_ATTR)) return null;
  el.setAttribute(MOUNTED_ATTR, "");

  const cfg = readConfig(el);
  renderLoading(el, cfg);

  let cards: ResourceCard[];
  try {
    cards = await fetchCards(cfg.endpoint);
  } catch (err) {
    renderError(el, err);
    return null;
  }

  clearLoading(el);

  // The island renders no wrapper of its own in embed mode, so the CSS scope
  // root has to be this container. Applied here rather than by React because
  // React owns the container's CHILDREN, not its attributes — and doing it this
  // way means the class sits on an element the Webflow page already has, adding
  // no depth. Every rule in `globals.css` is `.ct-resources <descendant>`, so
  // this is the whole of what keeps app styles applied and scoped.
  el.classList.add(SCOPE_CLASS);

  const root = createRoot(el);
  root.render(
    createElement(ResourcesIsland, {
      allCards: cards,
      initialFilter: cfg.initialFilter,
      initialQuery: cfg.initialQuery,
      initialSort: cfg.initialSort,
      // The Webflow page above the embed normally supplies the H1 and intro
      // copy, so the island renders neither unless `data-heading` asks for one.
      heading: cfg.heading ?? "",
      intro: cfg.intro ?? "",
      hideHeading: cfg.heading === null,
      embedded: true,
      syncUrl: cfg.syncUrl,
      pageSize: cfg.pageSize,
      onControlsReady: adoptControls,
    }),
  );

  watchHeight(el);
  return root;
}

/** Mount every container currently in the document. */
export function mountAll(): void {
  document
    .querySelectorAll<HTMLElement>(CONTAINER_SELECTOR)
    .forEach((el) => void mount(el));
}

// ── Boot ───────────────────────────────────────────────────────────────────
// A Webflow Embed element renders inline, so the container precedes this script
// in document order and is already parsed. The `readyState` guard covers the
// other placements — a custom code block in <head>, or Site Settings → Footer
// Code, where it is not.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountAll, { once: true });
} else {
  mountAll();
}

// ── Public API ─────────────────────────────────────────────────────────────
// `mount`/`mountAll` are for pages that build their container after load (a tab
// panel, a filtered CMS view); `mount()` is idempotent, so calling it on an
// already mounted element is safe.
//
// The setters are what Designer elements outside the container call. They are
// fire-and-forget by design: each validates its argument and falls back to a
// sane default rather than throwing, because the caller is a custom-code block
// on a Webflow page with no type checking and no error handling. A typo leaves
// the grid in its previous state instead of breaking the page.
//
//   window.ResourcesEmbed.setFilter('webinar')   // 'all' | one of the four types
//   window.ResourcesEmbed.setSort('oldest')      // 'latest' | 'oldest'
//   window.ResourcesEmbed.setQuery('b2b')
//   window.ResourcesEmbed.reset()
//   window.ResourcesEmbed.getState()             // null until ready
//
// State flows back out as two window events, both carrying a `ResourcesState`
// in `detail`: `resources-embed:ready` once, and `resources-embed:change` on
// every change after it. Read those to keep the active tab, the sort option and
// any result count in step — including when state changes from somewhere other
// than a click, such as a `?type=` deep link or `reset()`.

// `window.ResourcesEmbed` is created by the BUILD, not by this module.
// `build/embed-bundle.mjs` sets `build.lib.name = "ResourcesEmbed"`, so Rollup
// emits `var ResourcesEmbed = (function(exports){ … ; return exports })({})`
// and the global ends up being the module's exports object.
//
// **Everything callable from a Webflow page must therefore be a named export
// below.** Assigning `window.ResourcesEmbed = {…}` inside the module does not
// work: it runs during module evaluation, and the wrapper's own assignment
// replaces it moments later with the exports object.
//
// That mistake fails silently in the worst way. The build succeeds, the setters
// are present in the emitted file — grep finds them — and they are gone by the
// time any page can call one, leaving `window.ResourcesEmbed` holding just
// `{ mount, mountAll }` and a "setFilter is not a function" at the call site.
// It went unnoticed for as long as it did because the old module assigned that
// same `{ mount, mountAll }` shape by hand, so the clobbering was invisible.
//
// The setters are fire-and-forget by design: each validates its argument and
// falls back to a sane default rather than throwing, because the caller is a
// custom-code block on a Webflow page with no type checking and no error
// handling. A typo leaves the grid alone instead of breaking the page.
//
//   window.ResourcesEmbed.setFilter('webinar')   // 'all' | one of the four types
//   window.ResourcesEmbed.setSort('oldest')      // 'latest' | 'oldest'
//   window.ResourcesEmbed.setQuery('b2b')
//   window.ResourcesEmbed.reset()
//   window.ResourcesEmbed.getState()             // null until ready
//
// State flows back out as two window events, both carrying a `ResourcesState`
// in `detail`: `resources-embed:ready` once, and `resources-embed:change` on
// every change after it. Read those to keep the active tab, the sort option and
// any result count in step — including when state changes from somewhere other
// than a click, such as a `?type=` deep link or `reset()`.

/** Apply a type filter. Unknown values fall back to "all". */
export function setFilter(value: string): void {
  withControls((c) => c.setFilter(value));
}

/** Apply a search query. */
export function setQuery(value: string): void {
  withControls((c) => c.setQuery(value));
}

/** Apply an ordering. Unknown values fall back to "latest". */
export function setSort(value: string): void {
  withControls((c) => c.setSort(value));
}

/** Clear filter, query and sort back to defaults. */
export function reset(): void {
  withControls((c) => c.reset());
}

/** Current state, or `null` before the island has mounted. */
export function getState(): ResourcesState | null {
  return controls?.getState() ?? null;
}

/** Whether the setters apply immediately rather than being queued. */
export function isReady(): boolean {
  return controls !== null;
}

declare global {
  interface Window {
    ResourcesEmbed?: {
      mount: typeof mount;
      mountAll: typeof mountAll;
      setFilter: typeof setFilter;
      setQuery: typeof setQuery;
      setSort: typeof setSort;
      reset: typeof reset;
      getState: typeof getState;
      isReady: typeof isReady;
    };
  }
  interface WindowEventMap {
    "resources-embed:ready": CustomEvent<ResourcesState>;
    "resources-embed:change": CustomEvent<ResourcesState>;
  }
}
